import type { ReactNode } from 'react';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface HomeSectionHeading2026Props {
  title?: string | null;
  subtitle?: string | null;
  titleColor?: string;
  subtitleColor?: string;
  titleTag?: HeadingTag;
  subtitleTag?: HeadingTag;
  children?: ReactNode;
}

const DEFAULT_TITLE_COLOR = '#1a1c1e';
const DEFAULT_SUBTITLE_COLOR = '#626668';

const normalizeText = (value?: string | null) =>
  value
    ?.replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .trim() || '';

export default function HomeSectionHeading2026({
  title,
  subtitle,
  titleColor = DEFAULT_TITLE_COLOR,
  subtitleColor = DEFAULT_SUBTITLE_COLOR,
  titleTag: TitleTag = 'h2',
  subtitleTag: SubtitleTag = 'h5',
  children,
}: HomeSectionHeading2026Props) {
  const normalizedTitle = normalizeText(title);
  const normalizedSubtitle = normalizeText(subtitle);

  if (!normalizedTitle && !normalizedSubtitle && !children) return null;

  return (
    <header
      className={`relative flex w-full flex-col items-center justify-center gap-[8px] py-[20px] text-center xl:py-[28px] ${
        children ? 'min-h-[80px] px-[180px]' : 'px-[16px]'
      }`}
    >
      {normalizedTitle ? (
        <TitleTag
          className="w-full whitespace-pre-line break-words font-bb text-[24px] leading-[28px] xl:text-[32px] xl:leading-[39px]"
          style={{ color: titleColor }}
        >
          {normalizedTitle}
        </TitleTag>
      ) : null}

      {normalizedSubtitle ? (
        <SubtitleTag
          className="w-full whitespace-pre-line break-words font-br text-[14px] leading-[17px] xl:text-[20px] xl:leading-[24px]"
          style={{ color: subtitleColor }}
        >
          {normalizedSubtitle}
        </SubtitleTag>
      ) : null}

      {children ? (
        <div className="absolute right-[24px] top-1/2 -translate-y-1/2 xl:right-[48px]">
          {children}
        </div>
      ) : null}
    </header>
  );
}
